
import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useState } from "react";
import { Plus } from "lucide-react";
import Select from "react-select";
import { useAppServices } from "hook/services";
import { RoleOptions, RoleTypeOptions, UserPermissions } from "enums/user";
import { useUserInfo } from "context/user";

interface propTypes {
  handleRefresh: () => void;
  locations:any
}


const UserCreateModal = ({ handleRefresh ,locations}: propTypes) => {
  const AppService = useAppServices();
  const {user}=useUserInfo()
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggle = () => setShow((prev) => !prev);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    const payload = {
      username: values.username,
      email: values.email,
      password: values.password,
      phone: values.phone,
      roles: values.roles.map((r: any) => r.value),
      roleType: values.roleType.map((r: any) => r.value),
      permissions: values.permissions.map((p: any) => p.value),
      assignedLocations: values.assignedLocations.map((l: any) => l.value),
      companyId: values.companyId || null,
    };

    const { response, error } = await AppService.user.create({
      payload,
      toaster: true,
    });

    setLoading(false);

    if (response) {
      formik.resetForm();
      toggle();
      handleRefresh();
    }
  };

  const formik = useFormik({
    initialValues: {
      username: "",
      email: "",
      phone: "",
      password: "",
      roles: [],
      roleType: [],
      permissions: [],
      assignedLocations: [],
            companyId: user.companyId,
    },
    validationSchema: Yup.object({
      username: Yup.string().required("Please enter username"),
      email: Yup.string().email("Invalid email").required("Please enter email"),
      phone: Yup.string().required("Please enter phone number"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Please enter password"),
      roles: Yup.array().min(1, "Select at least one role"),
      roleType: Yup.array().min(1, "Select at least one role type"),
      permissions: Yup.array().min(1, "Select at least one permission"),
      assignedLocations: Yup.array().min(1, "Select at least one location"),
    }),
    onSubmit: handleSubmit,
  });

  return (
    <>
      <button
        type="button"
        className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600 focus:ring focus:ring-custom-100"
        onClick={toggle}
      >
        <Plus className="inline-block size-4 mr-1" />
        <span className="align-middle">Add User</span>
      </button>

      <Modal
        show={show}
        onHide={toggle}
        id="userModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[36rem] bg-white shadow rounded-md dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20">
          <Modal.Title className="text-16 font-semibold">
            Add New User
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label className="block mb-1 text-sm font-medium">Username</label>
              <input
                type="text"
                name="username"
                placeholder="Enter username"
                className="form-input w-full"
                onChange={formik.handleChange}
                value={formik.values.username}
              />
              {formik.touched.username && formik.errors.username && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.username}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block mb-1 text-sm font-medium">Email</label>
              <input
                type="email"
                name="email"
                placeholder="Enter email"
                className="form-input w-full"
                onChange={formik.handleChange}
                value={formik.values.email}
              />
              {formik.touched.email && formik.errors.email && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.email}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block mb-1 text-sm font-medium">Phone</label>
              <input
                type="text"
                name="phone"
                placeholder="Enter phone number"
                className="form-input w-full"
                onChange={formik.handleChange}
                value={formik.values.phone}
              />
              {formik.touched.phone && formik.errors.phone && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.phone}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block mb-1 text-sm font-medium">Password</label>
              <input
                type="password"
                name="password"
                placeholder="Enter password"
                className="form-input w-full"
                onChange={formik.handleChange}
                value={formik.values.password}
              />
              {formik.touched.password && formik.errors.password && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.password}
                </p>
              )}
            </div>

            {/* Roles */}
            <div>
              <label className="block mb-1 text-sm font-medium">Roles</label>
              <Select
                isMulti
                name="roles"
                options={RoleOptions}
                value={formik.values.roles}
                onChange={(selected) => formik.setFieldValue("roles", selected)}
              />
              {formik.touched.roles && formik.errors.roles && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.roles as string}
                </p>
              )}
            </div>

            {/* Role Type */}
            <div>
              <label className="block mb-1 text-sm font-medium">Role Type</label>
              <Select
                isMulti
                name="roleType"
                options={RoleTypeOptions}
                value={formik.values.roleType}
                onChange={(selected) =>
                  formik.setFieldValue("roleType", selected)
                }
              />
              {formik.touched.roleType && formik.errors.roleType && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.roleType as string}
                </p>
              )}
            </div>

            {/* Permissions */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                Permissions
              </label>
              <Select
                isMulti
                name="permissions"
                options={UserPermissions.map((item:any)=>({
                  label:item,
                  value:item,
                }))}
                value={formik.values.permissions}
                onChange={(selected) =>
                  formik.setFieldValue("permissions", selected)
                }
              />
              {formik.touched.permissions && formik.errors.permissions && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.permissions as string}
                </p>
              )}
            </div>

            {/* Assigned Locations */}
            <div>
              <label className="block mb-1 text-sm font-medium">
                Assigned Locations
              </label>
              <Select
                isMulti
                name="assignedLocations"
                options={locations.map((item:{name:string,_id:string})=>({
                  label:item.name,
                  value:item._id
                }))}
                value={formik.values.assignedLocations}
                onChange={(selected) =>
                  formik.setFieldValue("assignedLocations", selected)
                }
              />
              {formik.touched.assignedLocations &&
                formik.errors.assignedLocations && (
                  <p className="text-red-400 text-sm mt-1">
                    {formik.errors.assignedLocations as string}
                  </p>
                )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="text-red-500 bg-white border btn hover:text-red-600"
                onClick={() => {
                  formik.resetForm();
                  toggle();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
                disabled={loading}
              >
                {loading ? "Saving..." : "Create User"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UserCreateModal;
